import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import PerfectScrollbar from 'perfect-scrollbar';
import { Observable, Observer, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SubscriptionService } from '../../../models/subscription/subscription.service';

import { AccountType, Organization } from '../../../models/organization/organization.model';
import { OrganizationService } from '../../../models/organization/organization.service';
import { PaymentPlanType } from '../../../models/payment-plan/payment-plan.model';
import { SubscriptionModel, SubscriptionStatus } from '../../../models/subscription/subscription.model';
import { User } from '../../../models/user/user.model';
import { ErrorDialogService } from '../../../shared/error-dialog/error-dialog.service';
import { MenuItems } from '../../../shared/menu-items/menu-items';
import { ApiHelperService } from '../../api-helper.service';
import { AuthService } from '../../auth.service';
import { StateService } from '../../state.service';

@Component({
    selector: 'app-layout',
    templateUrl: './admin-layout.component.html',
    styleUrls: ['./admin-layout.component.scss'],
    standalone: false
})
export class AdminLayoutComponent implements OnInit, OnDestroy {

    private _router: Subscription;
    today: number = Date.now();

    isErrorDialogActive = false;
    apiErrorSubscription: Subscription;

    organizationSubscription: Subscription;
    userSubscription: Subscription;
    organization: Organization;
    isSetupComplete = false;
    isOrgDisabled = false;
    orgPaymentPlanType: PaymentPlanType | null = null;
    paymentSubscription: SubscriptionModel | undefined;
    isSelfServiceReview = false;

    user: User;
    isImpersonating = false;
    isAdminUser = false;
    isOrgAdminUser = false;
    isGroupAdminUser = false;
    isOrgBillingAdminUser = false;
    hasReviewerAccess = false;
    canAccessPortal = false;

    isTrialClosed = false;
    profileMenuOpen = false;
    manageMenuOpen = false;
    helpMenuOpen = false;

    url: string;
    root = 'ltr';

    psSidebar;
    psContent;

    @ViewChild('sidemenu', { static: false }) sidemenu;

    constructor(
        private api: ApiHelperService,
        private errorDialog: ErrorDialogService,
        private router: Router,
        public menuItems: MenuItems,
        public auth: AuthService,
        private apiHelper: ApiHelperService,
        private state: StateService,
        public errorDialogService: ErrorDialogService,
        public organizationService: OrganizationService,
        private subscriptionService: SubscriptionService,
        private changeDetectorRef: ChangeDetectorRef
    ) { }

    async ngOnInit() {
        this.organizationSubscription = this.state.subscribeToOrganization(
            {
                next: (o: Organization) => {
                    this.organization = o;
                    this.setOrganizationFields();
                    this.changeDetectorRef.detectChanges();
                }
            } as Observer<Organization>
        );

        this.userSubscription = this.state.subscribeToUser(
            {
                next: (u: User) => {
                    this.user = u;
                    this.setUserFields();
                    this.changeDetectorRef.detectChanges();
                }
            } as Observer<User>
        );
        const that = this; // eslint-disable-line

        try {
            this.user = await this.state.getUser() as User;
            this.setUserFields();
            this.changeDetectorRef.detectChanges();

            if (this.user?.id) {
                await this.auth.checkAccess(['portal-r', 'portal-w'], this.user.id!)
                    .then((res: Record<string, boolean>) => {
                        this.canAccessPortal = res['portal-r'];

                        if (this.canAccessPortal) {
                            this.menuItems!.getAll()!.find(el => el.state === 'portal')!
                                .children!
                                .forEach(child => {
                                    child.path += `?userId=${this.user.id}`;
                                });
                        }
                    });
            }

            this.organization = await this.state.getOrganization() as Organization;
            this.setOrganizationFields();
            this.changeDetectorRef.detectChanges();
        } catch (error) {
            await this.auth.sessionExpired();
            return;
        }

        this.apiErrorSubscription = this.apiHelper.apiErrorChange$.subscribe(apiError => {
            if (!that.isErrorDialogActive) {
                that.isErrorDialogActive = true;
                this.errorDialogService.open(apiError.title, apiError.message).subscribe(() => {
                    that.isErrorDialogActive = false;
                });
            }
        });

        const elemSidebar = <HTMLElement>document.querySelector('.app-inner > .sidebar-panel');
        const elemContent = <HTMLElement>document.querySelector('.app-inner > .mat-drawer-content');

        if (window.matchMedia(`(min-width: 960px)`).matches && !this.isMac()) {
            if (elemSidebar !== null) {
                this.psSidebar = new PerfectScrollbar(elemSidebar, { wheelSpeed: 2, suppressScrollX: true });
            }
            if (elemContent !== null) {
                this.psContent = new PerfectScrollbar(elemContent, { wheelSpeed: 2, suppressScrollX: true });
            }
        }

        const navEnd = this.router.events
            .pipe(filter(event => event instanceof NavigationEnd)) as Observable<NavigationEnd>;

        this._router = navEnd.subscribe((event: NavigationEnd) => {
            this.url = event.url;

            if (window.matchMedia(`(min-width: 960px)`).matches && !this.isMac()) {
                if (elemSidebar !== null) {
                    this.psSidebar = new PerfectScrollbar(elemSidebar, { wheelSpeed: 2, suppressScrollX: true });
                }
                if (elemContent !== null) {
                    this.psContent = new PerfectScrollbar(elemContent, { wheelSpeed: 2, suppressScrollX: true });
                }
            }
        });

        // We never want to end up on just '/manage'
        if (this.router.url === '/manage')
            this.auth.redirectAfterLogin();
    }

    @HostListener('click', ['$event'])
    onClick(_$event: Event) {
        // TODO MMD - fix weird bug with sidebar toggle not working
        setTimeout(() => {
            if (window.matchMedia(`(min-width: 960px)`).matches && !this.isMac()) {
                // BugFix/STAIT-649 and STAIT-658
                // These two lines were generating an error on a PC everytime because they were
                // being called before the completion of the ngOnInit function, so we'll check
                // to make sure the two elements are not null before calling them.
                if (this.psSidebar != null) {
                    this.psSidebar.update();
                }
                if (this.psContent != null) {
                    this.psContent.update();
                }
            }
        }, 350);
    }

    ngOnDestroy() {
        if (this._router) {
            this._router.unsubscribe();
        }
        if (this.apiErrorSubscription) {
            this.apiErrorSubscription.unsubscribe();
        }

        if (this.organizationSubscription) {
            this.organizationSubscription.unsubscribe();
        }
    }

    // TODO (jacob): remove these methods
    isInvoicePlanOrg() {
        return this.orgPaymentPlanType === PaymentPlanType.INVOICE;
    }

    isCreditPlanOrg() {
        return this.orgPaymentPlanType === PaymentPlanType.CREDITS;
    }

    primaryBillingLink(): string {
        return this.isInvoicePlanOrg()
            ? '/manage/billing'
            : '/manage/organization-settings/organization-billing';
    }
    
    isBillingNavActive(): boolean {
        const url = this.router.url;
        if (this.isInvoicePlanOrg()) {
            return url.includes('/manage/billing');
        }
        return url.includes('organization-billing');
    }

    isTrialPlanOrg() {
        return this.organization?.accountType === AccountType.TRIAL;
    }

    isClosedTrial() {
        return this.isTrialPlanOrg() && this.isTrialClosed;
    }
    isCreditAccountAlertVisible() {
        if (!this.url) {
            this.url = window.location.pathname;
        }
        return !this.url.includes('admin') && this.isSetupComplete && !this.isOrgDisabled && this.isCreditPlanOrg() && this.organization.remainingCreditCount! < 1;
    }

    isSubscriptionAccountAlertVisible() {
        if (!this.url) {
            this.url = window.location.pathname;
        }
        const showOutstandingAlert = this.paymentSubscription != null
            && (this.paymentSubscription as SubscriptionModel).status === SubscriptionStatus.OUTSTANDING
            && this.isOrgBillingAdminUser;

        return !this.url.includes('admin')
            && this.isSetupComplete
            && !this.isOrgDisabled
            && this.isInvoicePlanOrg()
            && this.paymentSubscription != null
            && ((this.paymentSubscription as SubscriptionModel).status === SubscriptionStatus.DELINQUENT
                || showOutstandingAlert);
    }

    isTrialAccountAlertVisible() {
        if (!this.url) {
            this.url = window.location.pathname;
        }
        const trialIsOnUpgradePage = this.isTrialPlanOrg() && this.url.includes('/manage/upgrade-account');

        return !this.url.includes('admin')
            && !trialIsOnUpgradePage
            && this.isTrialPlanOrg()
            && this.organization
            && (this.organization?.remainingDays! <= 3
                || this.organization?.remainingCreditCount! <= 2);
    }

    isSideNavAvailable() {
        return this.isAdminUser || (this.isSetupComplete && !this.isClosedTrial());
    }

    isOver(): boolean {
        // TODOEVENTUALLY MMD - make sure correct
        if (!this.url) {
            this.url = window.location.pathname;
        }
        if (this.url && (this.url.includes('trial-account-setup') || this.url.includes('statement-analysis') || this.url.includes('processing') || this.url.includes('edit-proposal'))) {
            return true;
        } else {
            return window.matchMedia(`(max-width: 960px)`).matches;
        }
    }

    isMac(): boolean {
        let bool = false;
        if (navigator.platform.toUpperCase().indexOf('MAC') >= 0 || navigator.platform.toUpperCase().indexOf('IPAD') >= 0) {
            bool = true;
        }
        return bool;
    }

    private setUserFields() {
        if (this.user == null) {
            this.isImpersonating = false;
            this.isAdminUser = false;
            this.isOrgAdminUser = false;
            this.isOrgBillingAdminUser = false;
            this.hasReviewerAccess = false;
            this.isGroupAdminUser = false;
            return;
        }

        this.isAdminUser = this.user.isAdmin ?? false;
        this.isOrgAdminUser = this.user.roleName === 'organizationAdmin';
        this.isOrgBillingAdminUser = this.user.roleName === 'organizationBillingAdmin';
        this.hasReviewerAccess = this.user.hasReviewerAccess ?? false;

        this.isGroupAdminUser = this.user.roleName === 'groupAdmin';

        // TODO (jacob): This logic is muddled && this routing 
        //               logic shouldn't be here.
        if (this.isAdminUser) {
            if (!this.url) {
                this.url = window.location.pathname;
            }

        } else if (this.user.isFromInvite && !this.user.inviteCompleted) {
            this.isSetupComplete = false;
            this.router.navigate(['/manage/invited-setup']);
        }
        this.changeDetectorRef.detectChanges();
    }

    // TODO (jacob): This whole function needs a rework
    private setOrganizationFields() {
        if (!this.organization) {

            this.isSetupComplete = false;
            this.isOrgDisabled = false;
            this.orgPaymentPlanType = null;
            this.isSelfServiceReview = false;
            this.isImpersonating = false;
            return;

        }

        if (this.isAdminUser)
            this.isImpersonating = true;
        else if (this.isClosedTrial()) {
            this.router.navigate(['/manage/upgrade-account']);
        } else if (this.url && (this.url === '/manage'
            || this.url.includes('account-setup')
            || this.url.includes('impersonate'))) {
            this.router.navigate(['/manage/merchants']);
        }

        this.isOrgDisabled = this.organization.disabled ?? false;
        // payment plan type can be null if setup is incomplete or if trial
        this.orgPaymentPlanType = this.organization.paymentPlanType
            ? this.organization.paymentPlanType
            : PaymentPlanType.TRIAL;

        this.isSelfServiceReview = this.organization.isSelfServiceReview ?? false;

        // TODO (jacob): just... why is this like this??
        this.isTrialClosed = this.organization.isTrialClosed as boolean ?? false;
        this.isSetupComplete = this.organization.isSetupComplete ?? false;

        // TODO (jacob): The valid plans are currently limited to just credit for some reason
        if (!this.organization.isSetupComplete) {
            if (this.organization.paymentPlanType === PaymentPlanType.INVOICE) {
                this.router.navigate(['/manage/enterprise-account-setup']);
            } /*else if (this.organization.accountType === AccountType.TRIAL) {
                this.router.navigate(['/manage/trial-account-setup']);
            }*/ else {
                this.router.navigate(['/manage/account-setup']);
            }
        }

        if (!this.url)
            this.url = window.location.pathname;

        switch (this.organization.paymentPlanType) {
            case PaymentPlanType.INVOICE:
                this.subscriptionService.get({ organizationId: this.organization.id! })
                    .then((subscription: SubscriptionModel | undefined) => this.paymentSubscription = subscription as SubscriptionModel)
                    .catch(async err => {
                        err.message += '\nIn admin-layout.setOrganizationFields';
                        err.message += '\nCould not get invoice org subscription';
                        await this.api.reportError(err);
                        console.error('We failed silently (from users perspective');
                    });
        }
        this.changeDetectorRef.detectChanges();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        const target = event.target as HTMLElement;
        if (!target.closest('.profile-dropdown')) {
            this.profileMenuOpen = false;
        }
        if (!target.closest('.main-header__nav-item')) {
            this.manageMenuOpen = false;
        }
        if (!target.closest('.help-dropdown')) {
            this.helpMenuOpen = false;
        }
    }

    isActive(route: string): boolean {
        return this.router.url.includes(route);
    }

    isActiveManage(): boolean {
        if (this.isBillingNavActive()) {
            return false;
        }
        const managePaths = ['organization-settings', 'statements', 'proposals', 'registration', 'users', 'client-management', 'billing-management', 'impersonate', 'user-settings'];
        return managePaths.some(path => this.router.url.includes(path));
    }

    isTeamLead(): boolean {
        return this.user?.roleName === 'teamLead';
    }

    isInAccountSetup(): boolean {
        return window.location.pathname.includes('account-setup');
    }
}
